import type {
  Scene,
  Texture,
  Mesh,
  HField,
  Material,
  TextureUUID,
  MeshUUID,
  Body,
  BodyUUID,
  ContactExclude,
  ContactPair,
  Geom,
  WorldBody,
  Joint,
} from "../../types/scene";
import { create } from "mutative";
import { generateUuid } from "../../utils/id";
import { normalizeActuatorRecord } from "./actuatorActions";
import type {
  SceneStoreSetState,
  SceneStoreGetState,
  SceneTransformCaches,
} from "../sceneStore.types";
import { createEmptyScene } from "../sceneDefaults";
import type { CoreActions, ResetSceneOptions } from "./actionTypes";
import { logger } from "../../utils/logger";
import { clearMjcfRootDefaultPoseRegistry } from "../../utils/mjcfRootDefaultPose";
import { getStandardFloorHField } from "../../utils/terrainFloor";
import { cleanupReferencesForDeletedTargets } from "./referenceCleanup";
import { shouldBlockSceneAuthoringMutation } from "../../utils/physicsGuards";
import { normalizeSceneOption } from "../../utils/mjcfOptions";
import {
  DEFAULT_COMPILER_SETTINGS,
  DEFAULT_STATISTIC_SETTINGS,
  normalizeCompilerSettings,
  normalizeStatisticSettings,
} from "../mjcfSettingsStore";
import { pruneStaleSelectionForScene } from "../../utils/pruneStaleSelection";

type LegacyBodyChild =
  | BodyUUID
  | string
  | number
  | { uuid?: string | number; name?: string | number }
  | null
  | undefined;
type LegacyBody = Omit<Body, "children"> & {
  children?: LegacyBodyChild[];
  [key: string]: unknown;
};
type LegacyBodyRecord = Record<string, LegacyBody>;
type BodyPositionCache = NonNullable<SceneTransformCaches["bodyPositions"]>;
type BodyRotationCache = NonNullable<SceneTransformCaches["bodyRotations"]>;
type SceneRestoreState = Scene & SceneTransformCaches;
type SceneRestoreUpdates = Partial<SceneRestoreState>;
type SceneRestoreKey = keyof SceneRestoreState;
type TextureMaterial = Material & { colorMap?: string };

const ensureTextureUuid = (texture: Texture): Texture => {
  if (texture.uuid) return texture;
  return { ...texture, uuid: generateUuid() as TextureUUID };
};

const ensureMeshUuid = (mesh: Mesh): Mesh => {
  if (mesh.uuid) return mesh;
  return { ...mesh, uuid: generateUuid() as MeshUUID };
};

const isBuiltinPublicTexture = (texture: Texture | undefined): boolean => {
  if (!texture) return false;
  const url = String(texture.url || "")
    .trim()
    .replace(/\\/g, "/");
  return url.startsWith("/textures/") || url.startsWith("textures/");
};

const normalizeContactPairs = (pairs: ContactPair[] | undefined): ContactPair[] =>
  Array.isArray(pairs)
    ? pairs
        .filter((pair) => pair && pair.geom1 && pair.geom2 && pair.geom1 !== pair.geom2)
        .map((pair) => ({
          ...pair,
          uuid: pair.uuid || generateUuid(),
        }))
    : [];

const normalizeContactExcludes = (excludes: ContactExclude[] | undefined): ContactExclude[] => {
  if (!Array.isArray(excludes)) return [];
  const seen = new Set<string>();
  const normalized: ContactExclude[] = [];
  excludes.forEach((exclude) => {
    if (!exclude?.body1 || !exclude?.body2) return;
    const body1 = String(exclude.body1) as BodyUUID;
    const body2 = String(exclude.body2) as BodyUUID;
    if (body1 === body2) return;
    const key = [body1, body2].sort().join("\0");
    if (seen.has(key)) return;
    seen.add(key);
    normalized.push({ body1, body2 });
  });
  return normalized;
};

let hasWarnedDeepEqualSerializationFailure = false;

function normalizeBodiesAndRoots(args: {
  bodies: Record<string, Body> | undefined;
  worldBodies: Scene["worldBodies"] | undefined;
  bodyPositions: SceneTransformCaches["bodyPositions"] | undefined;
  bodyRotations: SceneTransformCaches["bodyRotations"] | undefined;
}): {
  bodies: Record<string, Body> | undefined;
  worldBodies: Scene["worldBodies"] | undefined;
  bodyPositions: SceneTransformCaches["bodyPositions"] | undefined;
  bodyRotations: SceneTransformCaches["bodyRotations"] | undefined;
} {
  const inputBodies = args.bodies as unknown as LegacyBodyRecord | undefined;
  if (!inputBodies) {
    return {
      bodies: args.bodies,
      worldBodies: args.worldBodies,
      bodyPositions: args.bodyPositions,
      bodyRotations: args.bodyRotations,
    };
  }

  // Some legacy/import paths can key `bodies` by something other than `body.uuid`.
  // Normalize so every body has a uuid and the record is keyed by uuid.
  const keyToUuid: Record<string, BodyUUID> = {};
  const normalizedBodies: LegacyBodyRecord = {};

  // Pass 1: assign/collect uuids and build key mapping
  for (const [inputKey, body] of Object.entries(inputBodies)) {
    if (!body) continue;
    let uuid = (body.uuid || generateUuid()) as BodyUUID;
    if (normalizedBodies[uuid]) {
      // Extremely defensive: avoid collisions if snapshot contains duplicates/missing uuids
      const old = uuid;
      uuid = generateUuid() as BodyUUID;
      logger.warn(
        "[restoreState] Duplicate body uuid detected while normalizing snapshot; reassigning",
        {
          inputKey,
          oldUuid: old,
          newUuid: uuid,
          name: body.name,
        }
      );
    }
    keyToUuid[String(inputKey)] = uuid;
    if (body.uuid) keyToUuid[String(body.uuid)] = uuid;
    // Store a shallow copy now; children remap happens in pass 2.
    normalizedBodies[uuid] = { ...body, uuid };
  }

  // Pass 2: remap children arrays to normalized uuids
  for (const [uuid, body] of Object.entries(normalizedBodies)) {
    const children = Array.isArray(body.children) ? body.children : [];

    const toChildKey = (c: LegacyBodyChild): string | null => {
      if (c == null) return null;
      if (typeof c === "string" || typeof c === "number") return String(c);
      if (typeof c === "object") {
        const maybeUuid = c.uuid;
        if (typeof maybeUuid === "string" || typeof maybeUuid === "number")
          return String(maybeUuid);
        const maybeName = c.name;
        if (typeof maybeName === "string" || typeof maybeName === "number")
          return String(maybeName);
      }
      return null;
    };

    const remappedChildren: BodyUUID[] = [];
    for (const c of children) {
      const key = toChildKey(c);
      if (!key) continue;

      const mapped =
        (keyToUuid[key] as BodyUUID | undefined) ||
        (normalizedBodies[key] ? (key as BodyUUID) : undefined);

      if (!mapped || !normalizedBodies[mapped]) {
        logger.warn(
          "[restoreState] Dropping invalid body child reference during snapshot normalize",
          {
            parent: uuid,
            child: c,
            key,
            mapped,
          }
        );
        continue;
      }

      remappedChildren.push(mapped);
    }

    normalizedBodies[uuid] = { ...body, children: remappedChildren };
  }

  const computeRootsFromChildrenGraph = (): BodyUUID[] => {
    const bodyKeys = new Set(Object.keys(normalizedBodies));
    if (bodyKeys.size === 0) return [];
    const childKeys = new Set<string>();
    for (const b of Object.values(normalizedBodies)) {
      const kids = Array.isArray(b.children) ? b.children : [];
      for (const c of kids) {
        if (typeof c === "string" && bodyKeys.has(c)) childKeys.add(c);
      }
    }
    return Array.from(bodyKeys).filter((k) => !childKeys.has(k)) as BodyUUID[];
  };

  const computedRoots = computeRootsFromChildrenGraph();

  const normalizeWorldBodies = (
    worldBodies: Scene["worldBodies"] | undefined
  ): WorldBody[] | undefined => {
    const input = Array.isArray(worldBodies)
      ? (worldBodies as Array<WorldBody | BodyUUID | string>)
      : [];
    const out: WorldBody[] = [];
    const seen = new Set<string>();

    for (const wb of input) {
      const uuid = typeof wb === "string" ? wb : wb?.uuid;
      if (!uuid) continue;
      const mapped = keyToUuid[String(uuid)] || (uuid as BodyUUID);
      if (!normalizedBodies[mapped]) {
        logger.warn("[restoreState] worldBodies entry references missing body; dropping", {
          uuid,
          mapped,
        });
        continue;
      }
      if (seen.has(String(mapped))) continue;
      seen.add(String(mapped));
      const name = typeof wb === "string" ? undefined : wb?.name;
      out.push({ uuid: mapped, name: name ?? String(mapped) });
    }

    // Always prefer roots derived from the children graph when available.
    // This prevents the viewer from treating many links as world roots ("pulled apart" robot)
    // if snapshot `worldBodies` is stale/bloated.
    if (computedRoots.length > 0) {
      const nameByUuid = new Map<string, string>();
      for (const wb of out) nameByUuid.set(String(wb.uuid), wb.name);
      return computedRoots.map((uuid) => ({
        uuid,
        name: nameByUuid.get(String(uuid)) || String(uuid),
      }));
    }

    // If we have no computed roots, fall back to the snapshot list (already normalized).
    return out.length ? out : worldBodies;
  };

  const remapPositionCache = (
    cache: BodyPositionCache | undefined
  ): BodyPositionCache | undefined => {
    if (!cache) return cache;
    const next: BodyPositionCache = {};
    for (const [k, v] of Object.entries(cache)) {
      const mapped = keyToUuid[String(k)] || k;
      next[String(mapped)] = v;
    }
    return next;
  };

  const remapRotationCache = (
    cache: BodyRotationCache | undefined
  ): BodyRotationCache | undefined => {
    if (!cache) return cache;
    const next: BodyRotationCache = {};
    for (const [k, v] of Object.entries(cache)) {
      const mapped = keyToUuid[String(k)] || k;
      next[String(mapped)] = v;
    }
    return next;
  };

  // Ensure transform caches can't drift into a different space than body-local transforms.
  // If caches contain world-ish values (e.g. from a prior pose application), the viewer can
  // end up with correct joint angles but incorrect link spacing ("pulled apart").
  const syncPositionCacheFromBodies = (
    cache: BodyPositionCache | undefined
  ): BodyPositionCache | undefined => {
    if (!cache) return cache;
    const next: BodyPositionCache = {};
    for (const [k] of Object.entries(cache)) {
      const body = normalizedBodies[k];
      if (!body) continue;
      const v = body.position;
      if (Array.isArray(v) && v.length === 3) next[k] = [...v];
    }
    return next;
  };

  const syncRotationCacheFromBodies = (
    cache: BodyRotationCache | undefined
  ): BodyRotationCache | undefined => {
    if (!cache) return cache;
    const next: BodyRotationCache = {};
    for (const [k] of Object.entries(cache)) {
      const body = normalizedBodies[k];
      if (!body) continue;
      const v = body.orientation;
      if (Array.isArray(v) && v.length === 4) next[k] = [...v];
    }
    return next;
  };

  const remappedPositions = remapPositionCache(args.bodyPositions);
  const remappedRotations = remapRotationCache(args.bodyRotations);

  return {
    bodies: normalizedBodies as unknown as Record<string, Body>,
    worldBodies: normalizeWorldBodies(args.worldBodies),
    bodyPositions: syncPositionCacheFromBodies(remappedPositions),
    bodyRotations: syncRotationCacheFromBodies(remappedRotations),
  };
}

const normalizeBodyJointRefs = (
  bodies: Record<string, Body> | undefined,
  joints: Record<string, Joint> | undefined
): Record<string, Body> | undefined => {
  if (!bodies || !joints) return bodies;
  const jointUuidByName = new Map<string, string>();
  Object.entries(joints).forEach(([uuid, joint]) => {
    const name = typeof joint?.name === "string" ? joint.name.trim() : "";
    if (!name || jointUuidByName.has(name)) return;
    jointUuidByName.set(name, String(joint?.uuid || uuid));
  });

  let changed = false;
  const nextBodies: Record<string, Body> = {};
  Object.entries(bodies).forEach(([uuid, body]) => {
    const currentJoints = body?.joints as unknown[] | undefined;
    if (!body || !Array.isArray(currentJoints)) {
      nextBodies[uuid] = body;
      return;
    }
    const nextJoints: NonNullable<Body["joints"]> = currentJoints
      .map((entry: unknown) => {
        if (typeof entry === "string") return joints[entry] ? entry : null;
        if (!entry || typeof entry !== "object") return null;
        const ref = entry as { uuid?: unknown; name?: unknown };
        const jointUuid = typeof ref.uuid === "string" ? ref.uuid : "";
        if (jointUuid && joints[jointUuid]) return jointUuid;
        const name = typeof ref.name === "string" ? ref.name.trim() : "";
        return name ? jointUuidByName.get(name) || null : null;
      })
      .filter((entry): entry is string => Boolean(entry)) as NonNullable<Body["joints"]>;
    if (
      nextJoints.length !== currentJoints.length ||
      nextJoints.some((entry, index) => entry !== currentJoints[index])
    ) {
      changed = true;
      nextBodies[uuid] = { ...body, joints: nextJoints };
    } else {
      nextBodies[uuid] = body;
    }
  });
  return changed ? nextBodies : bodies;
};

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch (error) {
    if (!hasWarnedDeepEqualSerializationFailure) {
      hasWarnedDeepEqualSerializationFailure = true;
      logger.warn(
        "[coreActions] deepEqual failed to serialize value; falling back to non-equal",
        error
      );
    }
    return false;
  }
}

export const createCoreActions = (
  set: SceneStoreSetState,
  get: SceneStoreGetState
): CoreActions => ({
  // Core State Management
  resetScene: (options?: ResetSceneOptions) => {
    if (shouldBlockSceneAuthoringMutation("resetScene")) return;
    const deletedBodyUuidsForExternalCleanup = new Set<string>(Object.keys(get().bodies || {}));
    const standardFloorHField =
      options?.preserveTerrain === true ? getStandardFloorHField(get().hfields) : null;
    clearMjcfRootDefaultPoseRegistry();
    const result = set((state) => {
      const preservedBuiltinTextures: Record<string, Texture> = {};
      Object.entries(state.textures || {}).forEach(([key, texture]) => {
        if (!isBuiltinPublicTexture(texture)) return;
        preservedBuiltinTextures[key] = texture;
      });
      const empty = createEmptyScene();
      return {
        ...state,
        ...empty,
        textures: preservedBuiltinTextures,
        hfields:
          standardFloorHField && standardFloorHField.name
            ? { [String(standardFloorHField.name)]: standardFloorHField as HField }
            : empty.hfields,
      };
    });
    if (deletedBodyUuidsForExternalCleanup.size > 0) {
      cleanupReferencesForDeletedTargets({
        deletedBodyUuids: deletedBodyUuidsForExternalCleanup,
        external: { grasps: true },
      });
    }
    return result;
  },

  // Apply a history snapshot back to store state
  restoreState: (snapshot: Scene) => {
    const snapshotWithCaches = snapshot as SceneRestoreState;
    const current = get();

    const updates: SceneRestoreUpdates = {};

    const normalizeTextureRecord = (map: Record<string, Texture> | undefined) => {
      const byId: Record<string, Texture> = {};
      const keyMap: Record<string, TextureUUID> = {};
      Object.entries(map || {}).forEach(([inputKey, texture]) => {
        if (!texture) return;
        const withName = { ...texture, name: texture.name ?? inputKey } as Texture;
        const withUuid = ensureTextureUuid(withName);
        byId[withUuid.uuid] = withUuid;
        // allow resolving from prior keys
        keyMap[inputKey] = withUuid.uuid;
      });
      return { byId, keyMap };
    };

    const normalizeMeshRecord = (map: Record<string, Mesh> | undefined) => {
      const byId: Record<string, Mesh> = {};
      const keyMap: Record<string, MeshUUID> = {};
      Object.entries(map || {}).forEach(([inputKey, mesh]) => {
        if (!mesh) return;
        const withName = { ...mesh, name: mesh.name ?? inputKey } as Mesh;
        const withUuid = ensureMeshUuid(withName);
        if (Array.isArray(withUuid.scale) && withUuid.scale.length === 3) {
          withUuid.scale = [withUuid.scale[0], withUuid.scale[1], withUuid.scale[2]];
        }
        byId[withUuid.uuid] = withUuid;
        keyMap[inputKey] = withUuid.uuid;
      });
      return { byId, keyMap };
    };

    const { byId: texturesById, keyMap: textureKeyMap } =
      snapshot.textures === current.textures
        ? {
            byId: current.textures as Record<string, Texture>,
            keyMap: {} as Record<string, TextureUUID>,
          }
        : normalizeTextureRecord(snapshot.textures);
    const { byId: meshesById, keyMap: meshKeyMap } =
      snapshot.meshes === current.meshes
        ? { byId: current.meshes as Record<string, Mesh>, keyMap: {} as Record<string, MeshUUID> }
        : normalizeMeshRecord(snapshot.meshes);

    const remapMaterialTextureRefs = (
      materials?: Record<string, Material>
    ): Record<string, Material> => {
      if (!materials) return {};
      if (Object.keys(textureKeyMap).length === 0) return materials;
      let changed = false;
      const next: Record<string, Material> = {};
      Object.entries(materials).forEach(([matId, mat]) => {
        if (!mat) return;
        const colorMap = (mat as TextureMaterial).colorMap;
        if (colorMap && textureKeyMap[colorMap]) {
          changed = true;
          next[matId] = { ...mat, colorMap: textureKeyMap[colorMap] } as Material;
        } else {
          next[matId] = mat;
        }
      });
      return changed ? next : materials;
    };

    const remapBodiesMeshRefs = (
      bodies: Record<string, Body> | undefined
    ): Record<string, Body> | undefined => {
      if (!bodies) return bodies;
      let changed = false;
      const next: Record<string, Body> = {};
      Object.entries(bodies).forEach(([uuid, body]) => {
        if (!body) return;
        if (!Array.isArray(body.geoms)) {
          next[uuid] = body;
          return;
        }
        let geomChanged = false;
        const updatedGeoms = body.geoms.map((geom) => {
          if (!geom || typeof geom.mesh !== "string") return geom;
          const key = geom.mesh;
          const remapped = meshKeyMap[key];
          if (!remapped) return geom;
          geomChanged = true;
          return { ...geom, mesh: remapped } as Geom;
        }) as Geom[];
        if (geomChanged) {
          changed = true;
          next[uuid] = { ...body, geoms: updatedGeoms };
        } else {
          next[uuid] = body;
        }
      });
      return changed ? next : bodies;
    };

    const normalized = normalizeBodiesAndRoots({
      bodies: snapshot.bodies,
      worldBodies: snapshot.worldBodies,
      bodyPositions: snapshotWithCaches.bodyPositions,
      bodyRotations: snapshotWithCaches.bodyRotations,
    });
    const normalizeBackgroundColorHex = (value: unknown) => {
      if (typeof value !== "string") return "#111827";
      const match = /^#?([0-9a-f]{6})$/i.exec(value.trim());
      return match ? `#${match[1].toLowerCase()}` : "#111827";
    };
    const snapshotBackground =
      snapshot.background?.type === "color"
        ? {
            type: "color" as const,
            colorHex: normalizeBackgroundColorHex(snapshot.background.colorHex),
          }
        : (snapshot.background ?? { type: "builtin" as const });
    const snapshotBackgroundLibrary = { ...(snapshot.backgroundLibrary ?? {}) };
    if (snapshotBackground.type === "asset" && snapshotBackground.assetId) {
      snapshotBackgroundLibrary[snapshotBackground.assetId] = {
        ...snapshotBackgroundLibrary[snapshotBackground.assetId],
        assetId: snapshotBackground.assetId,
        ...(snapshotBackground.name ? { name: snapshotBackground.name } : {}),
        ...(snapshotBackground.fileName ? { fileName: snapshotBackground.fileName } : {}),
        ...(snapshotBackground.mimeType ? { mimeType: snapshotBackground.mimeType } : {}),
      };
    }

    const sanitizedSnapshot: SceneRestoreState = {
      ...snapshot,
      textures: texturesById,
      mjcfCompiler: normalizeCompilerSettings(snapshot.mjcfCompiler),
      option: normalizeSceneOption(snapshot.option),
      mjcfStatistic: normalizeStatisticSettings(snapshot.mjcfStatistic),
      materials: remapMaterialTextureRefs(snapshot.materials),
      meshes: meshesById,
      hfields: snapshot.hfields ?? {},
      background: snapshotBackground,
      backgroundLibrary: snapshotBackgroundLibrary,
      bodies:
        normalizeBodyJointRefs(
          remapBodiesMeshRefs(normalized.bodies) ?? normalized.bodies ?? snapshot.bodies,
          snapshot.joints as Record<string, Joint> | undefined
        ) ??
        normalized.bodies ??
        snapshot.bodies,
      lights: snapshot.lights ?? {},
      actuators:
        snapshot.actuators === current.actuators
          ? current.actuators
          : normalizeActuatorRecord(snapshot.actuators),
      tendons: snapshot.tendons ?? {},
      contactPairs: normalizeContactPairs(snapshot.contactPairs),
      contactExcludes: normalizeContactExcludes(snapshot.contactExcludes),
      worldBodies: normalized.worldBodies ?? snapshot.worldBodies,
      bodyPositions: normalized.bodyPositions ?? snapshotWithCaches.bodyPositions,
      bodyRotations: normalized.bodyRotations ?? snapshotWithCaches.bodyRotations,
    };

    if (current.uuid !== snapshot.uuid) {
      updates.uuid = snapshot.uuid;
    }

    const maybeSet = <K extends SceneRestoreKey>(key: K) => {
      const nextVal = sanitizedSnapshot[key];
      const currVal = current[key];
      if (!deepEqual(currVal, nextVal)) {
        updates[key] = nextVal;
      }
    };

    // Selectively update only changed slices to avoid heavy rebuilds
    maybeSet("name");
    maybeSet("bodies");
    maybeSet("worldBodies");
    maybeSet("bodyPositions");
    maybeSet("bodyRotations");
    maybeSet("materials");
    maybeSet("textures");
    maybeSet("meshes");
    maybeSet("hfields");
    maybeSet("joints");
    maybeSet("sites");
    maybeSet("cameras");
    maybeSet("lights");
    maybeSet("background");
    maybeSet("backgroundLibrary");
    maybeSet("mjcfCompiler");
    maybeSet("option");
    maybeSet("mjcfStatistic");
    maybeSet("actuators");
    maybeSet("tendons");
    maybeSet("equality");
    maybeSet("sensors");
    maybeSet("contactPairs");
    maybeSet("contactExcludes");
    if (Object.keys(updates).length > 0) {
      const previousBodyUuids = new Set<string>(Object.keys(current.bodies || {}));
      const nextBodyUuids = new Set<string>(Object.keys(sanitizedSnapshot.bodies || {}));
      const deletedBodyUuidsForExternalCleanup = new Set(
        Array.from(previousBodyUuids).filter((uuid) => !nextBodyUuids.has(uuid))
      );
      set((state) =>
        create(state, (draft) => {
          Object.assign(draft, updates);
        })
      );

      if (deletedBodyUuidsForExternalCleanup.size > 0) {
        cleanupReferencesForDeletedTargets({
          deletedBodyUuids: deletedBodyUuidsForExternalCleanup,
          external: { grasps: true },
        });
      }

      // Notify scene/material listeners of state restoration
      try {
        window.dispatchEvent(new CustomEvent("history-restored"));
      } catch (error) {
        logger.warn("[coreActions] Failed to dispatch history-restored event", error);
      }
    }
    pruneStaleSelectionForScene(get());
  },

  updateOption: (patch) => {
    if (shouldBlockSceneAuthoringMutation("updateOption")) return;
    set((state) =>
      create(state, (draft) => {
        draft.option = normalizeSceneOption({ ...(draft.option || {}), ...(patch || {}) });
      })
    );
  },

  updateMjcfCompiler: (patch) => {
    if (shouldBlockSceneAuthoringMutation("updateMjcfCompiler")) return;
    set((state) =>
      create(state, (draft) => {
        draft.mjcfCompiler = normalizeCompilerSettings({
          ...(draft.mjcfCompiler || {}),
          ...(patch || {}),
        });
      })
    );
  },

  updateMjcfStatistic: (patch) => {
    if (shouldBlockSceneAuthoringMutation("updateMjcfStatistic")) return;
    set((state) =>
      create(state, (draft) => {
        draft.mjcfStatistic = normalizeStatisticSettings({
          ...(draft.mjcfStatistic || {}),
          ...(patch || {}),
        });
      })
    );
  },

  resetMjcfCompiler: () => {
    if (shouldBlockSceneAuthoringMutation("resetMjcfCompiler")) return;
    set((state) =>
      create(state, (draft) => {
        draft.mjcfCompiler = normalizeCompilerSettings(DEFAULT_COMPILER_SETTINGS);
      })
    );
  },

  resetMjcfStatistic: () => {
    if (shouldBlockSceneAuthoringMutation("resetMjcfStatistic")) return;
    set((state) =>
      create(state, (draft) => {
        draft.mjcfStatistic = normalizeStatisticSettings(DEFAULT_STATISTIC_SETTINGS);
      })
    );
  },

  setBackground: (background) => {
    if (shouldBlockSceneAuthoringMutation("setBackground")) return;
    set((state) =>
      create(state, (draft) => {
        const colorHex =
          background?.type === "color" && typeof background.colorHex === "string"
            ? /^#?([0-9a-f]{6})$/i.exec(background.colorHex.trim())?.[1]
            : "";
        draft.background =
          background?.type === "asset" && background.assetId
            ? {
                type: "asset",
                assetId: background.assetId,
                ...(background.name ? { name: background.name } : {}),
                ...(background.fileName ? { fileName: background.fileName } : {}),
                ...(background.mimeType ? { mimeType: background.mimeType } : {}),
              }
            : colorHex
              ? { type: "color", colorHex: `#${colorHex.toLowerCase()}` }
              : { type: "builtin" };
        if (draft.background.type === "asset") {
          draft.backgroundLibrary = {
            ...(draft.backgroundLibrary || {}),
            [draft.background.assetId]: {
              assetId: draft.background.assetId,
              ...(draft.background.name ? { name: draft.background.name } : {}),
              ...(draft.background.fileName ? { fileName: draft.background.fileName } : {}),
              ...(draft.background.mimeType ? { mimeType: draft.background.mimeType } : {}),
            },
          };
        }
      })
    );
  },

  // Texture Management
  setTextures: (textures: Record<string, Texture>) => {
    if (shouldBlockSceneAuthoringMutation("setTextures")) return;
    set((state) =>
      create(state, (draft) => {
        const byId: Record<string, Texture> = {};
        const keyMap: Record<string, TextureUUID> = {};
        const fingerprintToUuid: Record<string, TextureUUID> = {};
        const nameUrlToUuid: Record<string, TextureUUID> = {};

        const toNameUrlKey = (texture: Texture): string => {
          const name = String(texture?.name || "")
            .trim()
            .toLowerCase();
          const url = String(texture?.url || "")
            .trim()
            .replace(/\\/g, "/")
            .toLowerCase();
          return `${name}::${url}`;
        };

        // Keep built-in texture catalog entries available across scene loads.
        Object.values((draft.textures as Record<string, Texture>) || {}).forEach((texture) => {
          if (!isBuiltinPublicTexture(texture)) return;
          const withUuid = ensureTextureUuid({ ...texture } as Texture);
          const fp = String(withUuid.fingerprint || "").trim();
          const nameUrlKey = toNameUrlKey(withUuid);
          const existingUuid =
            (fp && fingerprintToUuid[fp]) || (nameUrlKey && nameUrlToUuid[nameUrlKey]) || undefined;
          if (existingUuid) {
            keyMap[withUuid.uuid] = existingUuid;
            if (withUuid.name) keyMap[withUuid.name] = existingUuid;
            return;
          }
          byId[withUuid.uuid] = withUuid;
          keyMap[withUuid.uuid] = withUuid.uuid;
          if (withUuid.name) keyMap[withUuid.name] = withUuid.uuid;
          if (fp) fingerprintToUuid[fp] = withUuid.uuid;
          if (nameUrlKey) nameUrlToUuid[nameUrlKey] = withUuid.uuid;
        });

        Object.entries(textures).forEach(([inputKey, texture]) => {
          if (!texture) return;
          const withName = texture.name ? { ...texture } : { ...texture, name: inputKey };
          const incoming = withName as Texture;
          const fp = String(incoming.fingerprint || "").trim();
          const nameUrlKey = toNameUrlKey(incoming);
          const matchedUuid =
            (fp && fingerprintToUuid[fp]) || (nameUrlKey && nameUrlToUuid[nameUrlKey]) || undefined;
          const withUuid = matchedUuid
            ? ({ ...incoming, uuid: matchedUuid } as Texture)
            : ensureTextureUuid(incoming);
          byId[withUuid.uuid] = withUuid;
          keyMap[inputKey] = withUuid.uuid;
          if (withUuid.name) keyMap[withUuid.name] = withUuid.uuid;
          if (fp) fingerprintToUuid[fp] = withUuid.uuid;
          if (nameUrlKey) nameUrlToUuid[nameUrlKey] = withUuid.uuid;
        });
        draft.textures = byId;

        // Remap materials.colorMap from legacy keys (name/path) to texture UUIDs
        Object.entries(draft.materials as Record<string, Material>).forEach(([matId, mat]) => {
          if (!mat) return;
          const colorMap = (mat as TextureMaterial).colorMap;
          if (colorMap && keyMap[colorMap]) {
            (draft.materials as Record<string, Material>)[matId] = {
              ...mat,
              colorMap: keyMap[colorMap],
            } as Material;
          }
        });
      })
    );
  },

  // Mesh metadata management (keys may be names or UUIDs; normalize to UUID)
  upsertMeshes: (meshUpdates: Record<string, Partial<Mesh>>) => {
    if (shouldBlockSceneAuthoringMutation("upsertMeshes")) return;
    set((state) =>
      create(state, (draft) => {
        if (!meshUpdates) return;
        draft.meshes = draft.meshes || {};

        Object.entries(meshUpdates).forEach(([inputKey, update]) => {
          if (!inputKey || !update) return;
          const normalizedUpdate: Mesh = { ...(update as Mesh) };

          // If the update already has a UUID, prefer it (object library case)
          // Otherwise, try to find existing mesh or create new one
          let finalUuid: MeshUUID;
          let existing: Mesh | undefined;

          if (normalizedUpdate.uuid) {
            // Update already has UUID - use it
            finalUuid = normalizedUpdate.uuid;
            existing = draft.meshes[finalUuid] as Mesh | undefined;
          } else {
            // No UUID in update - ignore (UUID-only)
            return;
          }

          const cleanedExisting: Mesh = existing ? { ...existing } : ({ name: inputKey } as Mesh);
          const next: Mesh = {
            ...cleanedExisting,
            ...normalizedUpdate,
            name: normalizedUpdate.name || cleanedExisting.name || inputKey,
            uuid: finalUuid, // Use the UUID we determined above
          };

          // Explicitly preserve referenceOrientation if present in update
          if (normalizedUpdate.referenceOrientation !== undefined) {
            next.referenceOrientation = normalizedUpdate.referenceOrientation;
          }
          // Explicitly preserve referencePosition if present in update
          if (normalizedUpdate.referencePosition !== undefined) {
            next.referencePosition = normalizedUpdate.referencePosition;
          }

          if (Array.isArray(update.scale) && update.scale.length === 3) {
            next.scale = [update.scale[0], update.scale[1], update.scale[2]];
          }
          (draft.meshes as Record<string, Mesh>)[next.uuid] = next;
        });

        // UUID-only: no name-based remapping
      })
    );
  },

  setHFields: (hfields: Record<string, HField>) => {
    if (shouldBlockSceneAuthoringMutation("setHFields")) return;
    set((state) =>
      create(state, (draft) => {
        draft.hfields = hfields || {};
      })
    );
  },

  // Contact exclusion management
  setContactExcludes: (excludes) => {
    if (shouldBlockSceneAuthoringMutation("setContactExcludes")) return;
    set((state) =>
      create(state, (draft) => {
        draft.contactExcludes = normalizeContactExcludes(excludes as ContactExclude[]);
      })
    );
  },

  addContactExclude: (exclude) => {
    if (shouldBlockSceneAuthoringMutation("addContactExclude")) return;
    set((state) =>
      create(state, (draft) => {
        draft.contactExcludes = normalizeContactExcludes([
          ...((draft.contactExcludes || []) as ContactExclude[]),
          exclude,
        ]);
      })
    );
  },

  updateContactExclude: (index, patch) => {
    if (shouldBlockSceneAuthoringMutation("updateContactExclude")) return;
    set((state) =>
      create(state, (draft) => {
        const excludes = Array.isArray(draft.contactExcludes)
          ? ([...draft.contactExcludes] as ContactExclude[])
          : [];
        if (!Number.isInteger(index) || index < 0 || index >= excludes.length) return;
        excludes[index] = { ...excludes[index], ...(patch || {}) };
        draft.contactExcludes = normalizeContactExcludes(excludes);
      })
    );
  },

  removeContactExclude: (index) => {
    if (shouldBlockSceneAuthoringMutation("removeContactExclude")) return;
    set((state) =>
      create(state, (draft) => {
        const excludes = Array.isArray(draft.contactExcludes)
          ? ([...draft.contactExcludes] as ContactExclude[])
          : [];
        if (!Number.isInteger(index) || index < 0 || index >= excludes.length) return;
        excludes.splice(index, 1);
        draft.contactExcludes = normalizeContactExcludes(excludes);
      })
    );
  },

  setContactPairs: (pairs) => {
    if (shouldBlockSceneAuthoringMutation("setContactPairs")) return;
    set((state) =>
      create(state, (draft) => {
        draft.contactPairs = normalizeContactPairs(pairs as ContactPair[]);
      })
    );
  },

  addContactPair: (pair) => {
    if (shouldBlockSceneAuthoringMutation("addContactPair")) return generateUuid();
    const uuid = (pair.uuid || generateUuid()) as ContactPair["uuid"];
    set((state) =>
      create(state, (draft) => {
        draft.contactPairs = normalizeContactPairs([
          ...((draft.contactPairs || []) as ContactPair[]),
          { ...pair, uuid } as ContactPair,
        ]);
      })
    );
    return uuid;
  },

  updateContactPair: (pairUuid, patch) => {
    if (shouldBlockSceneAuthoringMutation("updateContactPair")) return;
    set((state) =>
      create(state, (draft) => {
        const pairs = Array.isArray(draft.contactPairs) ? draft.contactPairs : [];
        const idx = pairs.findIndex((pair: ContactPair) => String(pair.uuid) === String(pairUuid));
        if (idx === -1) return;
        pairs[idx] = { ...pairs[idx], ...(patch || {}), uuid: pairs[idx].uuid };
        draft.contactPairs = normalizeContactPairs(pairs);
      })
    );
  },

  removeContactPair: (pairUuid) => {
    if (shouldBlockSceneAuthoringMutation("removeContactPair")) return;
    set((state) =>
      create(state, (draft) => {
        draft.contactPairs = ((draft.contactPairs || []) as ContactPair[]).filter(
          (pair) => String(pair?.uuid) !== String(pairUuid)
        );
      })
    );
  },
});
